import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Space, Input, List, Row, Col, Tag, Grid, Select, message, Pagination } from 'antd';
import { PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { DataTable, AntDColumn } from '@/components/DataTable';
import { Parking } from '@/types/resources/property';
import { getParkings, addParking, updateParking, deleteParking } from '@/api/properties';
import { ParkingFormModal } from '@/components/features/resources/properties/ParkingFormModal';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';

const { useBreakpoint } = Grid;

// Constants
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export const Parkings: React.FC = () => {
  const { t } = useTranslation(['properties', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  // Check if this is the active tab
  const isActiveTab = location.pathname.includes('/properties/parkings');
  
  // State
  const [searchText, setSearchText] = useState('');
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingParking, setEditingParking] = useState<Partial<Parking> | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [lastDocs, setLastDocs] = useState<QueryDocumentSnapshot[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [parkingToDelete, setParkingToDelete] = useState<Parking | null>(null);

  // Queries
  const { data: parkingsData = { parkings: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery({
    queryKey: ['parkings', currentPage, pageSize, searchText, selectedBlock, selectedStatus, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      const lastDoc = currentPage > 1 ? lastDocs[currentPage - 2] : undefined;
      
      console.log('Fetching parkings:', { currentPage, pageSize, lastDoc, lastDocs: lastDocs.length });
      
      const result = await getParkings({
        searchTerm: searchText,
        pageSize,
        currentPage,
        lastDoc,
        firestoreUser
      });
      
      return result;
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: !!firestoreUser && isActiveTab, // Only run query if firestoreUser is available AND this is the active tab
  });

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (parkingsData.lastDoc) {
      setLastDocs(prev => {
        const newLastDocs = [...prev];
        newLastDocs[currentPage - 1] = parkingsData.lastDoc!;
        return newLastDocs;
      });
    }
  }, [parkingsData.lastDoc, currentPage]);

  // Reset lastDocs when page size changes
  React.useEffect(() => {
    setLastDocs([]);
    setCurrentPage(1);
  }, [pageSize]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setLastDocs([]);
  }, [searchText, selectedBlock, selectedStatus, pageSize]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (parking: Omit<Parking, 'id' | 'activityRecords' | 'workspace'>) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return addParking(parking, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:parkings.messages.addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('properties:parkings.messages.addError'));
      console.error('Error adding parking:', error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Parking> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateParking(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:parkings.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('properties:parkings.messages.updateError'));
      console.error('Error updating parking:', error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteParking(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:parkings.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
    },
    onError: (error) => {
      message.error(t('properties:parkings.messages.deleteError'));
      console.error('Error deleting parking:', error);
    }
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingParking(undefined);
    setIsModalVisible(true);
  }, []);

  const handleEdit = useCallback((record: Parking) => {
    setEditingParking(record);
    setIsModalVisible(true);
  }, []);

  const handleDelete = useCallback((record: Parking) => {
    setParkingToDelete(record);
    setShowDeleteModal(true);
  }, []);

  const handleBulkDelete = useCallback((_: any, selectedRows: Parking[]) => {
    // TODO: Implement bulk delete functionality
    console.log('Bulk delete parkings:', selectedRows);
  }, []);

  const handleModalSubmit = useCallback((values: Partial<Parking>) => {
    if (editingParking?.id) {
      updateMutation.mutate({ id: editingParking.id, data: values });
    } else {
      addMutation.mutate(values as Omit<Parking, 'id' | 'activityRecords' | 'workspace'>);
    }
  }, [editingParking, addMutation, updateMutation]);

  const handleViewDetails = useCallback((parking: Parking) => {
    navigate(`/resources/properties/parkings/${parking.id}`, { state: { parking } });
  }, [navigate]);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  // Memoized values
  const filteredData = useMemo(() => {
    if (!parkingsData.parkings) return [];
    // For demo mode, we'll do client-side filtering for block and status
    // In a real implementation, these would be passed to the API
    return parkingsData.parkings.filter((parking: Parking) => {
      const matchesBlock = !selectedBlock || parking.block?.id === selectedBlock;
      const matchesStatus = !selectedStatus || parking.status === selectedStatus;
      return matchesBlock && matchesStatus;
    });
  }, [parkingsData.parkings, selectedBlock, selectedStatus]);

  const uniqueBlocks = useMemo(() => 
    parkingsData.parkings
      .map(parking => parking.block)
      .filter((block): block is NonNullable<typeof block> => block !== undefined)
      .reduce((acc, block) => {
        if (!acc.some(b => b.value === block.id)) {
          acc.push({ value: block.id, label: block.name });
        }
        return acc;
      }, [] as Array<{ value: string; label: string }>), 
    [parkingsData.parkings]
  );

  const parkingColumns: AntDColumn<Parking>[] = [
    {
      title: t('properties:parkings.fields.number'),
      dataIndex: 'number',
      key: 'number',
      render: (text: string, record: Parking) => (
        <a onClick={() => handleViewDetails(record)}>{text}</a>
      ),
    },
    {
      title: t('properties:parkings.fields.block'),
      dataIndex: ['block', 'name'],
      key: 'block',
    },
    {
      title: t('properties:parkings.fields.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => t(`properties:parkings.types.${type}`),
    },
    {
      title: t('properties:parkings.fields.apartmentUnit'),
      dataIndex: ['apartmentUnit', 'number'],
      key: 'apartmentUnit',
    },
    {
      title: t('properties:parkings.fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => t(`properties:parkings.status.${status}`),
    },
  ];

  const statusOptions = [
    { value: 'available', label: t('properties:parkings.status.available') },
    { value: 'occupied', label: t('properties:parkings.status.occupied') },
    { value: 'maintenance', label: t('properties:parkings.status.maintenance') }
  ];

  const renderParkingCard = (parking: Parking) => {
    const statusColor = parking.status === 'occupied' ? 'green' : parking.status === 'maintenance' ? 'orange' : 'blue';
    const statusText = t(`properties:parkings.status.${parking.status}`);
    const typeText = t(`properties:parkings.types.${parking.type}`);
    const periodText = parking.pricing && parking.pricing.period
      ? t(`properties:parkings.pricing.period.${parking.pricing.period}`)
      : '-';

    return (
      <Card
        key={parking.id}
        hoverable
        style={{ marginBottom: 16 }}
        actions={[
          <Button
            key="view"
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(parking)}
          >
            {t('common:actions.view')}
          </Button>,
          <Button
            key="edit"
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(parking)}
          >
            {t('common:actions.edit')}
          </Button>,
          <Button
            key="delete"
            type="link"
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(parking)}
          >
            {t('common:actions.delete')}
          </Button>,
        ]}
      >
        <Card.Meta
          title={parking.number}
          description={
            <Space direction="vertical" size="small">
              <div>{parking.block?.name}</div>
              <div>{typeText}</div>
              {parking.apartmentUnit && (
                <div>
                  {t('properties:parkings.fields.apartmentUnit')}: {parking.apartmentUnit.number}
                </div>
              )}
              {parking.pricing && (
                <div>
                  {parking.pricing.amount} {parking.pricing.currency}/{periodText}
                </div>
              )}
              <div>
                <Tag color={statusColor}>
                  {statusText}
                </Tag>
              </div>
            </Space>
          }
        />
      </Card>
    );
  };

  return (
    <div style={{ width: '100%' }}>
      {isMobile ? (
        <>
          <List
            dataSource={filteredData}
            renderItem={renderParkingCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
            header={
              <>
                <Row className="search-filter-section" gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]} style={{ marginBottom: 16 }} align="middle">
                  <Col xs={24} sm={16} md={18}>
                    <Input
                      className="search-input"
                      id="parkings-search-input"
                      placeholder={t('common:searchPlaceholder', { entity: t('properties:parkings.title') })}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<SearchOutlined />}
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={8} md={6} style={{ textAlign: screens.xs ? 'left' : 'right', marginTop: screens.xs ? 8 : 0 }}>
                    <Button className="add-record-button" id="parkings-add-button" onClick={handleAdd} icon={<PlusOutlined />} block={screens.xs}>
                      {t('properties:parkings.actions.add')}
                    </Button>
                  </Col>
                </Row>
                <div style={{ 
                  marginBottom: 16,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  gap: '8px'
                }}>
                  <Select
                    placeholder={t('properties:parkings.fields.block')}
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={uniqueBlocks}
                  />
                  <Select
                    placeholder={t('properties:parkings.fields.status')}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={statusOptions}
                  />
                </div>
              </>
            }
            loading={isLoading}
            pagination={false}
          />
          <div className="pagination-section" style={{ 
            marginTop: 16, 
            textAlign: 'center',
            padding: '0 8px'
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={parkingsData.total}
              onChange={handlePageChange}
              showSizeChanger={!screens.xs}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showQuickJumper={!screens.xs}
              showTotal={!screens.xs ? (total: number, range: [number, number]) => 
                t('common:pagination.showing', { 
                  start: range[0], 
                  end: range[1], 
                  total 
                }) : undefined
              }
              size={screens.xs ? 'small' : 'default'}
              simple={screens.xs}
              style={{ 
                display: 'flex', 
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            />
          </div>
        </>
      ) : (
        <>
          <Card className="data-table-card">
            <DataTable
              className="data-table"
              dataSource={filteredData}
              columns={parkingColumns}
              loading={isLoading}
              rowKey="id"
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleViewDetails}
              onBulkDelete={handleBulkDelete}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: parkingsData.total,
                onChange: handlePageChange,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
                showTotal: (total: number, range: [number, number]) => 
                  t('common:pagination.showing', { 
                    start: range[0], 
                    end: range[1], 
                    total 
                  })
              }}
              headerActions={
                <Space>
                  <Input
                    className="search-input"
                    placeholder={t('common:searchPlaceholder', { entity: t('properties:parkings.title') })}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    style={{ width: 300 }}
                    allowClear
                  />
                  <Select
                    placeholder={t('properties:parkings.fields.block')}
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    allowClear
                    style={{ width: 150 }}
                    options={uniqueBlocks}
                  />
                  <Select
                    placeholder={t('properties:parkings.fields.status')}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ width: 150 }}
                    options={statusOptions}
                  />
                  <Button className="add-record-button" onClick={handleAdd} icon={<PlusOutlined />}>
                    {t('properties:parkings.actions.add')}
                  </Button>
                </Space>
              }
            />
          </Card>
        </>
      )}
      
      <ParkingFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleModalSubmit}
        parking={editingParking}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (parkingToDelete) {
            deleteMutation.mutate(parkingToDelete.id);
            setShowDeleteModal(false);
          }
        }}
        title={t('properties:parkings.messages.deleteConfirm')}
      />
    </div>
  );
}; 